import './contragents.css';
import { Button, Card, Checkbox, List, Popconfirm, Select, Tag, Tooltip, message } from 'antd';
import {
    CalendarOutlined, DeleteOutlined, EnvironmentOutlined, IdcardOutlined, MailOutlined, MergeCellsOutlined,
    PhoneOutlined, PlusOutlined, UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlFilters } from '@extensions/url-filters';
import { serverFetch } from '@server';
import { fmtDate, showError } from '@extensions/utils';
import { useDicts } from '@extensions/dicts';
import { PageTitle } from '@extensions/layout';
import FilterBar from '@controls/filter-bar/filter-bar';
import Contragent from './contragent';
import { LogoSlot } from './contragent-logo';

const PAGE = 30;
const FILTER_SPEC = {
    filter: { param: 'q', type: 'str', empty: '' },
    tag: { param: 'tag', type: 'int', empty: 0 },
    vid: { param: 'vid', type: 'int', empty: null },
    includeDeleted: { param: 'del', type: 'bool', empty: false },
    sort: { param: 'sort', type: 'str', empty: 'name' },
};

const SORT_OPTIONS = [
    { value: 'name', label: 'Наименование ↑' },
    { value: 'nameDesc', label: 'Наименование ↓' },
    { value: 'date', label: 'Дата регистрации ↑' },
    { value: 'dateDesc', label: 'Дата регистрации ↓' },
];

const cmpName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ru', { sensitivity: 'accent' });

const cmpDate = (a, b) => {
    if (!a.dateReg && !b.dateReg) return 0;
    if (!a.dateReg) return 1;
    if (!b.dateReg) return -1;
    if (a.dateReg === b.dateReg) return 0;
    return a.dateReg < b.dateReg ? -1 : 1;
};

const compareContragents = (sort) => (a, b) => {
    const desc = sort === 'nameDesc' || sort === 'dateDesc';
    if (sort === 'date' || sort === 'dateDesc') {
        const by = cmpDate(a, b);
        if (!a.dateReg || !b.dateReg || by === 0) return by === 0 ? a.id - b.id : by;
        return desc ? -by : by;
    }
    const byName = cmpName(a, b);
    if (byName !== 0) return desc ? -byName : byName;
    return a.id - b.id;
};

const IconRow = ({ icon, children, title }) => {
    if (!children) return null;
    return (
        <div className="contragent-card-row" title={typeof children === 'string' ? children : title}>
            {icon}
            <span className="contragent-card-ellipsis">{children}</span>
        </div>
    );
};

const ContragentCard = ({ record, tagById, checked, onOpen, onDelete, onCheck }) => (
    <Card
        size="small"
        hoverable
        className={`contragent-card${checked ? ' contragent-card-checked' : ''}`}
        style={record.isDel ? { opacity: 0.5 } : undefined}
        onClick={(e) => {
            if (record.isDel) {
                onOpen(record.id);
                return;
            }
            if (e.shiftKey || e.ctrlKey || e.metaKey) {
                onCheck(record.id, e.shiftKey ? true : !checked, e.shiftKey);
                return;
            }
            onOpen(record.id);
        }}
    >
        <div className="contragent-card-layout">
            <LogoSlot fileId={record.logoFileId} name={record.name} />
            <div className="contragent-card-body">
            <div className="contragent-card-name" title={record.name}>
                {!record.isDel && (
                    <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            checked={checked}
                            onChange={(e) => onCheck(record.id, e.target.checked, e.nativeEvent.shiftKey)}
                        />
                    </span>
                )}
                <span>{record.name}</span>
                {record.position && <span className="contragent-card-inn">{record.position}</span>}
                {record.isDel && <Tag color="default">удалён</Tag>}
                {record.inn && <span className="contragent-card-inn">ИНН {record.inn}</span>}
                <span className="contragent-card-actions" onClick={(e) => e.stopPropagation()}>
                    {record.dateReg && (
                        <span className="contragent-card-date" title="Дата регистрации">
                            <CalendarOutlined />
                            {fmtDate(record.dateReg)}
                        </span>
                    )}
                    <span className="contragent-card-id">#{record.id}</span>
                    {!record.isDel && (
                        <Popconfirm title="Удалить контрагента?" onConfirm={() => onDelete(record)} okText="Да" cancelText="Нет">
                            <DeleteOutlined className="row-delete-icon" />
                        </Popconfirm>
                    )}
                </span>
            </div>
            <IconRow icon={<IdcardOutlined />} title="Основной контакт">
                {record.primaryContactName
                    ? [record.primaryContactName, record.primaryContactPosition].filter(Boolean).join(', ')
                    : null}
            </IconRow>
            <IconRow icon={<PhoneOutlined />}>{record.phone || record.phone2 || record.primaryContactPhone}</IconRow>
            <IconRow icon={<MailOutlined />}>{record.email || record.email2}</IconRow>
            <IconRow icon={<EnvironmentOutlined />}>{record.address || record.address2}</IconRow>
            <IconRow icon={<UserOutlined />}>{record.userName}</IconRow>
            <span className="contragent-card-tags">
                {(record.tags ?? []).map((id) => {
                    const t = tagById.get(id);
                    return t ? (
                        <Tag key={id} color={t.bgColor || undefined} style={{ color: t.textColor || undefined }}>{t.name}</Tag>
                    ) : null;
                })}
            </span>
            {record.comment && <span className="contragent-card-comment" title={record.comment}>{record.comment}</span>}
        </div>
        </div>
    </Card>
);

const Contragents = () => {
    const { dicts } = useDicts();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(null); // 0 — новый
    const [checked, setChecked] = useState([]);
    const lastChecked = useRef(null);

    // Фильтры живут в URL: ?q=&tag=&vid=&del=1&sort=
    const [filters, setFilter] = useUrlFilters(FILTER_SPEC, 'contragentFilters');
    const sort = SORT_OPTIONS.some((o) => o.value === filters.sort) ? filters.sort : 'name';
    const rows = useMemo(() => [...items].sort(compareContragents(sort)), [items, sort]);

    const load = useCallback(() => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (filters.filter) qs.append('filter', filters.filter);
        if (filters.tag) qs.append('tag', filters.tag);
        if (filters.vid != null) qs.append('vid', filters.vid);
        if (filters.includeDeleted) qs.append('includeDeleted', 'true');
        serverFetch(`contragent?${qs.toString()}`)
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    // Подгрузка при прокрутке: показываем первые PAGE карточек, остальные — по мере появления «якоря» внизу
    const [visible, setVisible] = useState(PAGE);
    const sentinelRef = useRef(null);
    useEffect(() => { setVisible(PAGE); }, [items, sort]);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) setVisible((v) => Math.min(v + PAGE, items.length));
        }, { rootMargin: '200px' });
        observer.observe(el);
        return () => observer.disconnect();
    }, [items.length]);

    const onDelete = (record) => {
        serverFetch(`contragent/${record.id}`, { method: 'DELETE' })
            .then(load)
            .catch(showError);
    };

    const rowIds = rows.filter((r) => !r.isDel).map((r) => r.id);
    const check = (id, on, shift) => {
        const from = rowIds.indexOf(lastChecked.current);
        const to = rowIds.indexOf(id);
        const ids = shift && from >= 0 && to >= 0
            ? rowIds.slice(Math.min(from, to), Math.max(from, to) + 1)
            : [id];
        setChecked((c) => (on ? [...new Set([...c, ...ids])] : c.filter((x) => !ids.includes(x))));
        lastChecked.current = id;
    };

    const selected = rows.filter((r) => checked.includes(r.id) && !r.isDel);
    const keeper = selected.reduce((min, r) => (r.id < min.id ? r : min), selected[0]);
    const onMerge = () => {
        serverFetch('contragent/merge', { method: 'POST', bodyData: selected.map((r) => r.id) })
            .then((saved) => {
                message.success(`Объединено в #${saved.id}`);
                if (editId != null && selected.some((r) => r.id === editId)) setEditId(null);
                setChecked([]);
                load();
            })
            .catch(showError);
    };

    const filterDefs = useMemo(() => [
        {
            key: 'tag', label: 'Метка', emptyValue: 0,
            options: dicts.tags.filter((t) => t.vid === 1).map((t) => ({ value: t.id, label: t.name })),
        },
        {
            key: 'vid', label: 'Вид', emptyValue: null,
            options: [{ value: 1, label: 'Компании' }, { value: 0, label: 'Контакты' }],
        },
        {
            key: 'includeDeleted', label: 'Удалённые', emptyValue: false,
            options: [{ value: true, label: 'показывать' }],
        },
    ], [dicts.tags]);

    const tagById = useMemo(() => new Map(dicts.tags.map((t) => [t.id, t])), [dicts.tags]);

    return (
        <div>
            <div className="page-toolbar">
                <PageTitle>Контрагенты</PageTitle>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditId(0)}>Добавить</Button>
                {selected.length > 1 && keeper && (
                    <Tooltip title="Объединить контрагентов">
                        <Popconfirm
                            title={`Объединить ${selected.length} в «${keeper.name}» #${keeper.id}? Остальные будут удалены.`}
                            okText="Объединить"
                            cancelText="Отмена"
                            onConfirm={onMerge}
                        >
                            <Button icon={<MergeCellsOutlined />} />
                        </Popconfirm>
                    </Tooltip>
                )}
                <FilterBar
                    filters={filterDefs}
                    value={filters}
                    onChange={setFilter}
                    searchValue={filters.filter}
                    onSearch={(v) => setFilter({ filter: v })}
                    placeholder="Поиск контрагентов"
                />
                <Select
                    className="contragent-sort"
                    value={sort}
                    onChange={(value) => setFilter({ sort: value })}
                    options={SORT_OPTIONS}
                    popupMatchSelectWidth={false}
                    title="Сортировка"
                />
            </div>
            <List
                rowKey="id"
                loading={loading}
                dataSource={rows.slice(0, visible)}
                split={false}
                pagination={false}
                renderItem={(record) => (
                    <List.Item style={{ padding: '2px 0' }}>
                        <ContragentCard
                            record={record}
                            tagById={tagById}
                            checked={checked.includes(record.id)}
                            onOpen={setEditId}
                            onDelete={onDelete}
                            onCheck={check}
                        />
                    </List.Item>
                )}
            />
            <div ref={sentinelRef} className="contragents-sentinel">
                {visible < rows.length ? 'Загрузка…' : (rows.length > 0 && `Всего: ${rows.length}`)}
            </div>
            {editId !== null && (
                <Contragent id={editId} onClose={() => { setEditId(null); load(); }} />
            )}
        </div>
    );
};

export default Contragents;
